import { Command } from 'cmdk';
import { useMemo, useCallback, useState, useRef } from 'react';
import type { IconName } from '@customerio/pluma-icons';
import {
  TextField,
  Box,
  ModalPrimitiveDialog,
  ModalPrimitiveOverlay,
  ModalPrimitive,
  EmptyStateDescription,
  EmptyState,
  EmptyStateTitle,
  Icon,
  Text,
  Flex,
  FormattedDateTime,
  EmptyStateGraphic,
  Spinner,
  KeyboardShortcut,
  InlineStack,
  type ModalPrimitiveProps,
  List,
  ListItem,
  Label,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { useIsAgentFullscreen } from '@/features/copilot';
import type { useCopilotSessionContext } from '@/features/copilot';
import { open as openSidebar } from '@/features/sidebar';

import { useServicesQuery } from '@/services/api';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import type { useCurrentWorkspace } from '@/context/current-workspace';
import * as styles from './index.css';
import type { SearchResult } from '@/services/auth/types';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { LABEL } from './constants';
import { computeItemPosition, getIconNameFromSearchResult } from './utils';
import { useStoredHydraCommands } from './use-stored-hydra-commands';
import { useLocalSearchResults } from './use-local-search-results';
import { SEARCH_RESULT_HEADINGS } from './constants';
import { groupSearchResults } from './utils';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import {
  PlumaModal as PlumaModalCss,
  PlumaBreadcrumbs as PlumaBreadcrumbsCss,
} from '@customerio/pluma-components/css/components';
import type { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { useKeyHold } from '@tanstack/react-hotkeys';

type CommandDialogData = {
  session: ReturnType<typeof useCopilotSessionContext>;
  workspace: ReturnType<typeof useCurrentWorkspace>;
  initialInputValue?: string;
  persistInputValue: (value: string) => void;
  track: ReturnType<typeof useAnalytics>['track'];
};

export function CommandDialog({ data, onClose, modalState }: PlumaManagedModalProps<CommandDialogData>) {
  const { isOpen: isDialogOpen, onClose: onModalManagerClose, onCloseComplete, modalContext } = modalState;
  const isUniversalSearchEnabled = useFeatureFlag(AccountFeatureFlag.UniversalSearchDialog);
  const isAiAssistantV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const isAiEnabled = useFeatureFlag(AccountFeatureFlag.AiEnabled);
  const isAgentFullscreen = useIsAgentFullscreen();
  const showAskAssistant = isAiAssistantV2 && isAiEnabled && !isAgentFullscreen;
  const safeNavigate = useSafeNavigate();
  const [inputValue, setInputValue] = useState(data.initialInputValue ?? '');
  const [debouncedInputValue] = useDebouncedValue(inputValue.trim(), 250);

  const isMetaHeld = useKeyHold('Meta');
  const isCtrlHeld = useKeyHold('Control');

  const { session, workspace, persistInputValue, track } = data;
  const workspaceId = workspace?.id ?? 0;

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      persistInputValue(value);
    },
    [persistInputValue],
  );

  const handleInputFocus = useCallback((event: Event & { target: HTMLInputElement }) => {
    event.target.select();
  }, []);

  const queryStartTimeRef = useRef<number | null>(null);
  const previousDebouncedInputRef = useRef(debouncedInputValue);

  const openTimeRef = useRef(Date.now());
  const previousIsDialogOpenRef = useRef(isDialogOpen);

  // used for tracking the time the user started typing to get the time to first result
  if (debouncedInputValue !== previousDebouncedInputRef.current) {
    previousDebouncedInputRef.current = debouncedInputValue;
    if (debouncedInputValue.length > 0) {
      queryStartTimeRef.current = Date.now();
    }
  }
  // used for tracking the time the dialog was opened to get the time open
  if (isDialogOpen !== previousIsDialogOpenRef.current) {
    previousIsDialogOpenRef.current = isDialogOpen;
    if (isDialogOpen === true) {
      openTimeRef.current = Date.now();
    }
  }

  const {
    data: searchData,
    isLoading,
    isError,
    dataUpdatedAt,
  } = useServicesQuery(
    'search',
    { params: { workspaceId }, query: { q: debouncedInputValue } },
    {
      enabled: isUniversalSearchEnabled && isDialogOpen === true && debouncedInputValue.length > 0 && workspaceId > 0,
      staleTime: 1000 * 30, // Consider search results stale after 30 seconds
      retryOnMount: false,
    },
  );

  const closeDialog = useCallback(() => {
    onClose();
  }, [onClose]);

  const groupedResults = useMemo(() => groupSearchResults(searchData?.results ?? []), [searchData]);

  // Using inputValue instead of debouncedInputValue as we aren't calling
  // an expensive API like /search and there probably won't be many commands to filter by
  const hydraCommands = useStoredHydraCommands(inputValue);

  // Local search results — handlers are called on-demand with the debounced query
  const localSearchGroups = useLocalSearchResults(debouncedInputValue);

  const apiResultsCount = searchData?.results?.length ?? 0;
  const localSearchCount = localSearchGroups.reduce((sum, g) => sum + g.results.length, 0);

  const hasNoApiResults =
    searchData == null || (searchData != null && (searchData.results == null || searchData.results.length === 0));
  const hasLocalCommands = hydraCommands.length > 0;
  const hasLocalSearchResults = localSearchGroups.length > 0;
  const hasNoResults = hasNoApiResults && !hasLocalCommands && !hasLocalSearchResults;
  const askAssistantVisible =
    showAskAssistant && debouncedInputValue.length > 0 && !isLoading && !hasNoResults && !isError;

  const totalVisibleCount = useMemo(() => {
    return (askAssistantVisible ? 1 : 0) + hydraCommands.length + localSearchCount + apiResultsCount;
  }, [askAssistantVisible, hydraCommands.length, localSearchCount, apiResultsCount]);

  const timeToFirstResultMs =
    queryStartTimeRef.current != null ? Math.max(0, dataUpdatedAt - queryStartTimeRef.current) : 0;

  const handleItemSelect = useCallback(
    (url: string, kind: SearchResult['kind'], position: number) => {
      const openInNewTab = isMetaHeld || isCtrlHeld;

      void track('universalSearch', 'itemSelected', {
        'Item Type': 'search_result',
        'Result Kind': kind,
        'Open in New Tab': openInNewTab,
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Time to First Result Ms': timeToFirstResultMs,
      });
      if (openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        safeNavigate(url);
        closeDialog();
      }
    },
    [
      safeNavigate,
      closeDialog,
      track,
      isMetaHeld,
      isCtrlHeld,
      debouncedInputValue,
      dataUpdatedAt,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
    ],
  );

  const handleLocalCommandSelect = useCallback(
    (onSelect: () => void | Promise<void>, position: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'local_command',
        'Result Kind': 'command',
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [closeDialog, track, debouncedInputValue, totalVisibleCount, apiResultsCount, timeToFirstResultMs],
  );

  const handleLocalSearchSelect = useCallback(
    (onSelect: () => void | Promise<void>, position: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'local_search',
        'Result Kind': 'search',
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [closeDialog, track, debouncedInputValue, totalVisibleCount, apiResultsCount, timeToFirstResultMs],
  );

  const handleAskAssistant = useCallback(
    (resultPosition: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'ask_assistant',
        'Result Kind': 'assistant',
        Query: debouncedInputValue,
        'Result Position': resultPosition,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
      });
      openSidebar('copilot');
      if (debouncedInputValue.length > 0) {
        void session.sendMessage(debouncedInputValue).catch(sentryCaptureException);
      }
      closeDialog();
    },
    [closeDialog, track, debouncedInputValue, session, totalVisibleCount, apiResultsCount, timeToFirstResultMs],
  );

  const handleDialogClose = useCallback<NonNullable<ModalPrimitiveProps['onClose']>>(
    (_event, reason) => {
      void track('universalSearch', 'closed', {
        Reason: reason ?? 'dismiss',
        'Had Query': !isEmptyString(inputValue.trim()),
        'Time Open Ms': Date.now() - openTimeRef.current,
      });
      onModalManagerClose?.(_event, reason);
    },
    [track, onModalManagerClose, inputValue],
  );

  return (
    <ModalPrimitive
      initialFocusIndex={-1}
      animationTransitionDuration={125}
      isOpen={isDialogOpen ?? false}
      onClose={handleDialogClose}
      onCloseComplete={onCloseComplete}
      modalContext={modalContext}
    >
      <ModalPrimitiveOverlay className={PlumaModalCss.classNames.modalOverlay}>
        <ModalPrimitiveDialog className={styles.dialog}>
          <Command loop label={LABEL} shouldFilter={false}>
            <Command.Input asChild value={inputValue} onValueChange={handleInputChange}>
              <TextField
                autoFocus={true}
                placeholder={LABEL}
                onInput={(event) => handleInputChange(event.target.value)}
                onFocus={handleInputFocus}
                ariaLabel="Use the command palette to search for campaigns, templates, segments, or anything else in this workspace."
                className={`${styles.largeTextField} fs-mask`}
                icon="search"
                iconColor="accent"
                size="sm"
                ml="200"
                mr={hasNoResults ? '200' : undefined}
                mt="100"
              />
            </Command.Input>
            <Command.List>
              {hasLocalCommands ? (
                <Command.Group heading="Quick actions">
                  {hydraCommands.map((cmd, cmdIndex) => (
                    <Command.Item
                      key={`local-${cmd.id}`}
                      value={`local-${cmd.id}`}
                      onSelect={() =>
                        handleLocalCommandSelect(
                          cmd.onSelect,
                          computeItemPosition({
                            hydraCommandsCount: hydraCommands.length,
                            localSearchCount,
                            section: 'quickActions',
                            groupOffset: 0,
                            itemIndex: cmdIndex,
                          }),
                        )
                      }
                      asChild
                    >
                      <Box alignItems="flex-start" gap="050" display="flex">
                        <Icon
                          mt="025"
                          size="sm"
                          sizeVersion="v2"
                          color="base"
                          name={(cmd.icon ?? 'command') as IconName}
                        />
                        <Flex direction="column">
                          <Text text="product-p">{cmd.label}</Text>
                          {cmd.description != null ? (
                            <Text size="xxs" color="subtle">
                              {cmd.description}
                            </Text>
                          ) : null}
                        </Flex>
                      </Box>
                    </Command.Item>
                  ))}
                </Command.Group>
              ) : null}
              {hasLocalSearchResults ? (
                <>
                  {localSearchGroups.map((group, groupIndex) => {
                    const groupOffset = localSearchGroups
                      .slice(0, groupIndex)
                      .reduce((sum, g) => sum + g.results.length, 0);
                    return (
                      <Command.Group key={`search-${group.heading}`} heading={group.heading}>
                        {group.results.map((result, resultIndex) => (
                          <Command.Item
                            key={`search-${result.id}`}
                            value={`search-${result.id}`}
                            onSelect={() =>
                              handleLocalSearchSelect(
                                result.onSelect,
                                computeItemPosition({
                                  hydraCommandsCount: hydraCommands.length,
                                  localSearchCount,
                                  section: 'localSearch',
                                  groupOffset,
                                  itemIndex: resultIndex,
                                }),
                              )
                            }
                            asChild
                          >
                            <Box alignItems="flex-start" gap="050" display="flex">
                              <Icon
                                mt="025"
                                size="sm"
                                sizeVersion="v2"
                                color="base"
                                name={(result.icon ?? 'search') as IconName}
                              />
                              <Flex direction="column">
                                <Text text="product-p">{result.label}</Text>
                                {result.description != null ? (
                                  <Text size="xxs" color="subtle">
                                    {result.description}
                                  </Text>
                                ) : null}
                              </Flex>
                            </Box>
                          </Command.Item>
                        ))}
                      </Command.Group>
                    );
                  })}
                </>
              ) : null}
              {(hasLocalCommands || hasLocalSearchResults) && isLoading ? (
                <Command.Loading asChild>
                  <EmptyState>
                    <EmptyStateGraphic>
                      <Spinner className={styles.spinner} color="information" />
                    </EmptyStateGraphic>
                    <EmptyStateTitle>Searching in {workspace?.name ?? 'this workspace'}…</EmptyStateTitle>
                  </EmptyState>
                </Command.Loading>
              ) : null}
              {!hasLocalCommands && !hasLocalSearchResults && isLoading ? (
                <Command.Loading asChild>
                  <EmptyState>
                    <EmptyStateGraphic>
                      <Spinner className={styles.spinner} color="information" />
                    </EmptyStateGraphic>
                    <EmptyStateTitle>
                      Searching for "<span className="fs-mask">{debouncedInputValue}</span>"
                    </EmptyStateTitle>
                    <EmptyStateDescription>
                      Hang on while we search in {workspace?.name ?? 'this workspace'}
                    </EmptyStateDescription>
                  </EmptyState>
                </Command.Loading>
              ) : null}
              {debouncedInputValue.length === 0 && !hasLocalCommands && !hasLocalSearchResults ? (
                <Command.Empty asChild>
                  <EmptyState>
                    <EmptyStateTitle icon="search">Search {workspace?.name ?? 'Workspace'}</EmptyStateTitle>
                    <EmptyStateDescription>
                      Try searching for campaigns, templates, segments, or anything else in this workspace.
                    </EmptyStateDescription>
                  </EmptyState>
                </Command.Empty>
              ) : null}
              {debouncedInputValue.length > 0 && !isLoading && (hasNoResults || isError) ? (
                <Command.Empty asChild>
                  <EmptyState>
                    <EmptyStateTitle icon="search">
                      No results found for <span className="fs-mask">{debouncedInputValue}</span>
                    </EmptyStateTitle>
                    <EmptyStateDescription>Try adjusting your search to find matches.</EmptyStateDescription>
                  </EmptyState>
                </Command.Empty>
              ) : null}
              {searchData?.results != null ? (
                <>
                  {groupedResults.map(([kind, items], groupIndex) => {
                    const groupOffset = groupedResults
                      .slice(0, groupIndex)
                      .reduce((sum, [, groupItems]) => sum + groupItems.length, 0);
                    return (
                      <Command.Group
                        key={kind}
                        heading={SEARCH_RESULT_HEADINGS?.[kind] ?? `Search Results for '${kind}'`}
                      >
                        {items.map((item, index) => {
                          const url = new URL(item.url);
                          const safeUrlValue = url.pathname + url.search;
                          const hasDescription = item.kind === 'campaign' || item.kind === 'segment';
                          const showBreadcrumbs =
                            item.kind === 'action' && isEmptyString(item.other.campaign_name) === false;
                          const key = `${item.kind}-${index}-${item.doc_id ?? encodeURIComponent(item.url)}`;

                          // TODO add logic on a per item basis to determine if the label should be shown
                          const hasLabel = false;

                          return (
                            <Command.Item
                              onSelect={() =>
                                handleItemSelect(
                                  safeUrlValue,
                                  item.kind,
                                  computeItemPosition({
                                    hydraCommandsCount: hydraCommands.length,
                                    localSearchCount,
                                    section: 'apiResults',
                                    groupOffset,
                                    itemIndex: index,
                                  }),
                                )
                              }
                              key={key}
                              value={key}
                              asChild
                            >
                              <Box alignItems="flex-start" gap="050" display="flex">
                                <Icon
                                  mt="025"
                                  name={getIconNameFromSearchResult(item)}
                                  size="sm"
                                  sizeVersion="v2"
                                  color="base"
                                />
                                <Flex direction="column" gap="025">
                                  <Flex direction="column">
                                    {hasLabel ? (
                                      <InlineStack alignBlock="center" gap="100">
                                        <Text text="product-p" className="fs-mask">
                                          {item.title}
                                        </Text>
                                        {/* TODO add logic on a per item basis to determine what label should be shown */}
                                        <Label>Test</Label>
                                      </InlineStack>
                                    ) : (
                                      <Text text="product-p" className="fs-mask">
                                        {item.title}
                                      </Text>
                                    )}
                                    {hasDescription && item.other?.description != null ? (
                                      <Text size="xxs" color="subtle" className="fs-mask">
                                        {item.other.description}
                                      </Text>
                                    ) : null}
                                  </Flex>
                                  {showBreadcrumbs ? (
                                    <List styleType="none" className={PlumaBreadcrumbsCss.classNames.breadcrumbsList}>
                                      <ListItem className={PlumaBreadcrumbsCss.classNames.breadcrumbsItem}>
                                        Campaigns
                                      </ListItem>
                                      <ListItem className={PlumaBreadcrumbsCss.classNames.breadcrumbsItem}>
                                        {item.other.campaign_name}
                                      </ListItem>
                                    </List>
                                  ) : null}
                                  {item.other?.created_at != null && item.other.created_at != null ? (
                                    <Text size="xxs" color="subtle">
                                      Created at{' '}
                                      <FormattedDateTime
                                        className="fs-mask"
                                        date={item.other.created_at}
                                        format="medium"
                                      />
                                    </Text>
                                  ) : null}
                                </Flex>
                              </Box>
                            </Command.Item>
                          );
                        })}
                      </Command.Group>
                    );
                  })}
                </>
              ) : null}
              {askAssistantVisible ? (
                <Command.Item
                  key="ask-assistant"
                  value="ask-assistant"
                  onSelect={() => handleAskAssistant(hydraCommands.length + localSearchCount + apiResultsCount + 1)}
                  asChild
                >
                  <Box alignItems="flex-start" gap="050" display="flex">
                    <Icon mt="025" size="sm" sizeVersion="v2" color="base" name="sparkle" />
                    <Flex direction="column">
                      <Text text="product-p">Ask Agent</Text>
                      <Text size="xxs" color="subtle">
                        Send &ldquo;<span className="fs-mask">{debouncedInputValue}</span>&rdquo; to the AI Agent
                      </Text>
                    </Flex>
                  </Box>
                </Command.Item>
              ) : null}
            </Command.List>
          </Command>
          <InlineStack
            w="full"
            alignInline="space-between"
            py="100"
            px="200"
            alignBlock="center"
            className={styles.footer}
          >
            <Flex gap="050" alignItems="center">
              <KeyboardShortcut hotkey="Escape" />
              <Text size="xxs" color="subtle">
                Close
              </Text>
            </Flex>

            <InlineStack alignInline="space-between" gap="300">
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="ArrowUp" />
                <KeyboardShortcut hotkey="ArrowDown" />
                <Text size="xxs" color="subtle">
                  Navigate
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Enter" />
                <Text size="xxs" color="subtle">
                  Select
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Mod+Enter" />
                <Text size="xxs" color="subtle">
                  Open in new tab
                </Text>
              </Flex>
            </InlineStack>
          </InlineStack>
        </ModalPrimitiveDialog>
      </ModalPrimitiveOverlay>
    </ModalPrimitive>
  );
}
